import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback
} from 'react';
import { X } from 'lucide-react';
import Barcode from 'react-barcode';
import { useSwipeable } from 'react-swipeable';
import SecureImage from './SecureImage';
import StatusIndicator from './StatusIndicator';
import { validateEAN8, validateEAN13, validateUPC12, digitsOnly } from '../utils/barcodeUtils';
import { resolveCardStatus } from '../utils/pidStatus';

import {
  Overlay,
  Panel,
  Header,
  Title,
  CloseButton,
  CarouselWrapper,
  TwoColumnSection,
  LeftColumn,
  RightColumn,
  HighlightedTitle,
  DetailGrid,
  StyleGrid,
  StyleCard,
  BarcodeWrapper,
  InfoRowSplit,
  InfoRow,
  InfoLabel,
  InfoValue,
  SectionDivider,
  CarouselNavButton,
} from '../styles/PackageIndexStyles';

const ItemDetailPanel = ({ group, onClose }) => {
  const panelRef = useRef();
  const [current, setCurrent] = useState(0);
  const [showSwipeHint, setShowSwipeHint] = useState(true);

  const { MethodName, Items = [], Images = [], Picture } = group;
  const fallbackImage = 'MethodFallback.webp';

  const first = Items[0] || {};
  const {
    ItemNumber,
    Description,
    CasesBinPerPallet,
    Ti,
    Hi,
    Length,
    Width,
    Height,
    CaseNetWeight,
    UPC,
    Retailer,
    Type
  } = first;

  const imagesToDisplay = useMemo(() => {
    return Array.isArray(Images) && Images.length > 0
      ? Images
      : Picture
        ? [Picture]
        : [fallbackImage];
  }, [Images, Picture]);

  // Preload images
  useEffect(() => {
    imagesToDisplay.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [imagesToDisplay]);

  const { codeDigits, codeFormat } = useMemo(() => {
    const d = digitsOnly(UPC);
    if (d.length === 8 && validateEAN8(d)) return { codeDigits: d, codeFormat: 'EAN8' };
    if (d.length === 12 && validateUPC12(d)) return { codeDigits: d, codeFormat: 'UPC' };
    if (d.length === 13 && validateEAN13(d)) return { codeDigits: d, codeFormat: 'EAN13' };
    return { codeDigits: null, codeFormat: null };
  }, [UPC]);

  const max = imagesToDisplay.length;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrent((c) => (c + 1) % max),
    onSwipedRight: () => setCurrent((c) => (c - 1 + max) % max),
    trackMouse: true,
  });

  const handleClickOutside = useCallback((e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
  }, [onClose]);

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    const timeout = setTimeout(() => setShowSwipeHint(false), 2500);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      clearTimeout(timeout);
    };
  }, [handleClickOutside, handleEscape]);

  return (
    <Overlay>
      <Panel ref={panelRef}>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <Title>{MethodName}</Title>
            <StatusIndicator status={resolveCardStatus(group)} />
          </div>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <TwoColumnSection>
          <LeftColumn>
            <CarouselWrapper {...swipeHandlers}>
            <div
              style={{ position: 'relative' }}
              className="image-container"
            >
              {max > 1 && current > 0 && (
                <CarouselNavButton
                  className="hover-show"
                  left
                  onClick={() => setCurrent((current - 1 + max) % max)}
                >
                  &#8249;
                </CarouselNavButton>
              )}
              {max > 1 && current < max - 1 && (
                <CarouselNavButton
                  className="hover-show"
                  right
                  onClick={() => setCurrent((current + 1) % max)}
                >
                  &#8250;
                </CarouselNavButton>
              )}

              <SecureImage
                src={imagesToDisplay[current]}
                folder="PIDMethods"
                alt={`Image ${current + 1}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />

              {max > 1 && (
                <>
                  <SecureImage
                    src={imagesToDisplay[(current + 1) % max]}
                    folder="PIDMethods"
                    alt=""
                    style={{ display: 'none' }}
                  />
                  <SecureImage
                    src={imagesToDisplay[(current - 1 + max) % max]}
                    folder="PIDMethods"
                    alt=""
                    style={{ display: 'none' }}
                  />
                  <div
                    className="hover-show"
                    style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '12px',
                      color: 'var(--white)',
                      fontSize: '0.85rem',
                      background: 'var(--shadow-a35)',
                      padding: '2px 6px',
                      borderRadius: '6px',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  >
                    {current + 1} / {max}
                  </div>
                </>
              )}

              {showSwipeHint && max > 1 && (
                <div
                  className="hover-show"
                  style={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '0.75rem',
                    color: 'var(--white)',
                    background: 'var(--shadow-a35)',
                    padding: '4px 10px',
                    borderRadius: '10px',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  Swipe to view
                </div>
              )}
            </div>

            <style>
              {`
                .image-container .hover-show {
                  opacity: 0;
                  pointer-events: none;
                }
                .image-container:hover .hover-show {
                  opacity: 1 !important;
                  pointer-events: auto;
                }
              `}
            </style>
          </CarouselWrapper>


            <DetailGrid>
              {!codeDigits ? (
                <InfoRow>
                  <InfoLabel>NO UPC</InfoLabel>
                </InfoRow>
              ) : (
                <BarcodeWrapper>
                  <Barcode
                    value={codeDigits}
                    format={codeFormat}
                    height={60}
                    width={1.5}
                    displayValue
                    background="transparent"
                    font="OCRB"
                  />
                </BarcodeWrapper>
              )}

              {Description && (
                <InfoRow>
                  <InfoLabel>Item Description</InfoLabel>
                  <InfoValue>{Description}</InfoValue>
                </InfoRow>
              )}
              {Retailer && (
                <InfoRow>
                  <InfoLabel>Retailer</InfoLabel>
                  <InfoValue>{Retailer}</InfoValue>
                </InfoRow>
              )}
              {ItemNumber && (
                <InfoRow>
                  <InfoLabel>Item Number</InfoLabel>
                  <InfoValue>{ItemNumber}</InfoValue>
                </InfoRow>
              )}

              {(Ti && Hi && CasesBinPerPallet) && (
                <InfoRowSplit>
                  <div className="column left">
                    <InfoLabel>TI × HI</InfoLabel>
                    <InfoValue>{Ti} × {Hi}</InfoValue>
                  </div>
                  <div className="vertical-divider" />
                  <div className="column right">
                    <InfoLabel>Cases Per Pallet</InfoLabel>
                    <InfoValue>{CasesBinPerPallet}</InfoValue>
                  </div>
                </InfoRowSplit>
              )}

              {CaseNetWeight && (
                <InfoRow>
                  <InfoLabel>Case Net Weight</InfoLabel>
                  <InfoValue>{CaseNetWeight} lb</InfoValue>
                </InfoRow>
              )}
              {(Length && Width && Height) && (
                <InfoRow>
                  <InfoLabel>Dimensions (L × W × H)</InfoLabel>
                  <InfoValue>{Length} in × {Width} in × {Height} in</InfoValue>
                </InfoRow>
              )}
              {Type && (
                <InfoRow>
                  <InfoLabel>Type</InfoLabel>
                  <InfoValue>{Type}</InfoValue>
                </InfoRow>
              )}
            </DetailGrid>
          </LeftColumn>
          <SectionDivider />

          <RightColumn style={{ overflowY: 'auto', maxHeight: '70vh' }}>
            {Object.entries(
              group.Items.reduce((acc, item) => {
                const commodity = item.Commodity;
                if (!acc[commodity]) acc[commodity] = [];
                if (!acc[commodity].includes(item.Style)) {
                  acc[commodity].push(item.Style);
                }
                return acc;
              }, {})
            ).map(([commodity, styles]) => (
              <div key={commodity} style={{ marginBottom: '1rem' }}>
                <HighlightedTitle>{commodity}</HighlightedTitle>
                <StyleGrid>
                  {styles.map((style) => (
                    <StyleCard key={style}>
                      <h4>{style}</h4>
                    </StyleCard>
                  ))}
                </StyleGrid>
              </div>
            ))}
          </RightColumn>

        </TwoColumnSection>
      </Panel>
    </Overlay>
  );
};

export default React.memo(ItemDetailPanel);
